/**
 * ArticleWrite — /articles/new and /articles/:slug/edit.
 * Rich long-form composer for ChatsPulse. Uses TipTap-based RichEditor,
 * with title, subtitle, cover-image URL, category, tags, and language
 * declaration.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Save, Send, Loader2, MapPin } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import RichEditor from "@/components/RichEditor";
import useTransliterate from "@/hooks/useTransliterate";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const CATEGORIES = [
  "local-news", "opinion", "travel", "food", "farming", "business",
  "education", "health", "nature", "science", "technology", "sports",
  "movies", "music", "real-estate", "jobs", "events", "family-alumni",
  "transport", "community",
];

const INPUT_LANG_HINTS = [
  { code: "en", label: "English" },
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "pt", label: "Portuguese" },
  { code: "ru", label: "Russian" },
  { code: "ar", label: "Arabic" },
  { code: "he", label: "Hebrew" },
  { code: "hi", label: "Hindi" },
  { code: "ml", label: "Malayalam" },
  { code: "ta", label: "Tamil" },
  { code: "te", label: "Telugu" },
  { code: "kn", label: "Kannada" },
  { code: "bn", label: "Bengali" },
  { code: "mr", label: "Marathi" },
  { code: "gu", label: "Gujarati" },
  { code: "pa", label: "Punjabi" },
  { code: "ur", label: "Urdu" },
  { code: "zh", label: "Chinese" },
  { code: "ja", label: "Japanese" },
  { code: "ko", label: "Korean" },
];

export default function ArticleWrite() {
  const { slug } = useParams(); // if present, editing
  const nav = useNavigate();
  const { user } = useAuth();

  const [articleId, setArticleId] = useState(null);
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [coverImageUrl, setCoverImageUrl] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("community");
  const [tags, setTags] = useState("");
  const [language, setLanguage] = useState("en");
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!!slug);

  const titleRef = useRef(null);
  const subtitleRef = useRef(null);

  const titleTranslit = useTransliterate({
    value: title, onChange: setTitle, inputRef: titleRef, testIdPrefix: "compose-title-translit",
  });
  const subtitleTranslit = useTransliterate({
    value: subtitle, onChange: setSubtitle, inputRef: subtitleRef, testIdPrefix: "compose-subtitle-translit",
  });

  // Load existing article when editing
  useEffect(() => {
    if (!slug) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/articles/${slug}`);
        if (!alive) return;
        setArticleId(data.id);
        setTitle(data.title || "");
        setSubtitle(data.subtitle || "");
        setCoverImageUrl(data.cover_image_url || "");
        setBody(data.body_html || "");
        setCategory(data.category || "community");
        setTags((data.tags || []).join(", "));
        setLanguage(data.language || "en");
        setCountry(data.location?.country || "");
        setState(data.location?.state || "");
        setCity(data.location?.city || "");
      } catch (e) {
        toast.error("Failed to load article");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  const canSubmit = useMemo(
    () => title.trim().length >= 3 && body.replace(/<[^>]*>/g, "").trim().length >= 1,
    [title, body],
  );

  const save = async (status) => {
    if (!user) {
      toast.error("Please sign in to publish");
      nav("/auth/socialpulse-login");
      return;
    }
    if (!canSubmit) {
      toast.error("Add a title and some content first");
      return;
    }
    setBusy(true);
    try {
      const cleanTags = tags.split(",").map((t) => t.trim()).filter(Boolean).slice(0, 12);
      const loc = { country: country.trim(), state: state.trim(), city: city.trim() };
      Object.keys(loc).forEach((k) => !loc[k] && delete loc[k]);
      const payload = {
        title: title.trim(),
        subtitle: subtitle.trim() || null,
        body_html: body,
        cover_image_url: coverImageUrl.trim() || null,
        category,
        tags: cleanTags.length ? cleanTags : null,
        language,
        location: Object.keys(loc).length ? loc : null,
        status,
      };
      if (articleId) {
        await axios.patch(`${API}/articles/${articleId}`, payload, { withCredentials: true });
        toast.success(status === "published" ? "Published" : "Draft saved");
        nav(`/articles/${slug}`);
      } else {
        const { data } = await axios.post(`${API}/articles`, payload, { withCredentials: true });
        toast.success(status === "published" ? "Published" : "Draft saved");
        nav(`/articles/${data.slug}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center text-slate-500" data-testid="article-write-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        <p className="mt-3 text-sm">Loading article…</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 lg:px-6 py-8" data-testid="article-write">
      <Link to="/articles" className="text-xs uppercase tracking-widest text-slate-400 hover:text-blue-700 font-semibold inline-flex items-center gap-1" data-testid="article-write-back">
        <ArrowLeft className="w-3 h-3" /> All articles
      </Link>

      <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold">
        {articleId ? "Edit article" : "New article"}
      </div>

      {/* Title */}
      <div className="mt-4 relative">
        <input
          ref={titleRef}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={titleTranslit.onKeyDown}
          placeholder="Your headline…"
          className="w-full text-3xl sm:text-4xl font-black tracking-tight text-slate-900 placeholder-slate-300 border-none outline-none py-2 bg-transparent pr-16"
          data-testid="article-write-title"
        />
        <div className="absolute right-2 top-4">{titleTranslit.chip}</div>
      </div>

      {/* Subtitle */}
      <div className="mt-2 relative">
        <input
          ref={subtitleRef}
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          onKeyDown={subtitleTranslit.onKeyDown}
          placeholder="Optional subtitle or deck…"
          className="w-full text-lg text-slate-600 placeholder-slate-300 border-none outline-none py-1 bg-transparent pr-16"
          data-testid="article-write-subtitle"
        />
        <div className="absolute right-2 top-2">{subtitleTranslit.chip}</div>
      </div>

      {/* Cover image URL */}
      <div className="mt-4">
        <label className="block text-xs font-bold text-slate-700 mb-1">Cover image URL (optional)</label>
        <input
          value={coverImageUrl}
          onChange={(e) => setCoverImageUrl(e.target.value)}
          placeholder="https://…"
          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-400"
          data-testid="article-write-cover"
        />
        {coverImageUrl && (
          <img src={coverImageUrl} alt="Cover preview" className="mt-2 max-h-56 rounded-lg border border-slate-200 object-cover"
               onError={(e) => { e.currentTarget.style.display = "none"; }}
               data-testid="article-write-cover-preview" />
        )}
      </div>

      {/* Rich editor */}
      <div className="mt-4">
        <label className="block text-xs font-bold text-slate-700 mb-1">Body</label>
        <RichEditor
          value={body}
          onChange={setBody}
          placeholder="Write your article…"
          testIdPrefix="article-write-editor"
        />
      </div>

      {/* Metadata */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
        <label className="block">
          <span className="block text-xs font-bold text-slate-700 mb-1">Category</span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-400 bg-white"
            data-testid="article-write-category"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c.replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase())}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="block text-xs font-bold text-slate-700 mb-1">Article language</span>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-400 bg-white"
            data-testid="article-write-language"
          >
            {INPUT_LANG_HINTS.map((l) => (
              <option key={l.code} value={l.code}>{l.label} ({l.code})</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="block text-xs font-bold text-slate-700 mb-1">Tags (comma-separated)</span>
          <input
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="travel, food, kerala"
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-400"
            data-testid="article-write-tags"
          />
        </label>
      </div>

      <fieldset className="rounded-xl border border-slate-200 p-4 mt-4">
        <legend className="px-2 text-xs font-bold text-slate-700 inline-flex items-center gap-1">
          <MapPin className="w-3 h-3 text-blue-600" /> Location (optional)
        </legend>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-1">
          <input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Country" className="px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-400" data-testid="article-write-country" />
          <input value={state}   onChange={(e) => setState(e.target.value)}   placeholder="State / Province" className="px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-400" data-testid="article-write-state" />
          <input value={city}    onChange={(e) => setCity(e.target.value)}    placeholder="City / Town" className="px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-400" data-testid="article-write-city" />
        </div>
      </fieldset>

      <div className="flex items-center justify-between pt-6 gap-3">
        <p className="text-xs text-slate-500">
          {user
            ? `Author: ${user.name || user.email || user.id}`
            : "You'll be asked to sign in before publishing."}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!canSubmit || busy}
            onClick={() => save("draft")}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200 disabled:opacity-50"
            data-testid="article-write-save-draft"
          >
            <Save className="w-3.5 h-3.5" /> Save Draft
          </button>
          <button
            type="button"
            disabled={!canSubmit || busy}
            onClick={() => save("published")}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
            data-testid="article-write-publish"
          >
            <Send className="w-3.5 h-3.5" />
            {busy ? "Saving…" : "Publish"}
          </button>
        </div>
      </div>
    </div>
  );
}
